import { Controller } from "@hotwired/stimulus"

// Persisted "user dismissed the popup" flag. Keyed once at module scope so the
// popup banner stays gone for good across navigations/reloads until the user
// enables notifications (or clears storage).
const DISMISS_KEY = "notif-popup-dismissed"

// Opts this device into Web Push booking alerts. On a user gesture it asks for
// notification permission, subscribes via the pushManager, and POSTs the
// subscription to the server. Progressive enhancement: the prompt/enabled/popup
// states start hidden and are revealed only for browsers that support push.
export default class extends Controller {
  static targets = ["prompt", "enabled", "popup"]

  connect() {
    this.supported =
      "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
    this.render()
  }

  render() {
    const permission = this.supported ? Notification.permission : "unsupported"
    const undecided = this.supported && permission === "default"
    if (this.hasPromptTarget) this.promptTarget.hidden = !undecided
    if (this.hasEnabledTarget) this.enabledTarget.hidden = !(this.supported && permission === "granted")
    if (this.hasPopupTarget) this.popupTarget.hidden = !(undecided && !this.dismissed)
  }

  dismiss(event) {
    event.preventDefault()
    try {
      localStorage.setItem(DISMISS_KEY, "1")
    } catch {
      // Safari private mode throws on setItem; popup just reappears next load.
    }
    this.render()
  }

  // Safari private mode throws on localStorage access; treat any failure as
  // "not dismissed" so the popup still works, just without persistence.
  get dismissed() {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1"
    } catch {
      return false
    }
  }

  async subscribe(event) {
    event.preventDefault()
    if (!this.supported) return

    const permission = await Notification.requestPermission()
    if (permission !== "granted") return this.render()

    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.applicationServerKey()
    })
    await this.post(subscription)
    this.render()
  }

  async post(subscription) {
    const keys = subscription.toJSON().keys
    await fetch("/push_subscription", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.metaContent("csrf-token")
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        p256dh_key: keys.p256dh,
        auth_key: keys.auth
      })
    })
  }

  applicationServerKey() {
    const base64 = this.metaContent("vapid-public-key")
    const padding = "=".repeat((4 - (base64.length % 4)) % 4)
    const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/")
    const raw = atob(normalized)
    return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)))
  }

  metaContent(name) {
    return document.querySelector(`meta[name="${name}"]`)?.content
  }
}
